<script lang="ts">
  import { tStore } from '../../../i18n'
  import Tooltip from '../../../components/Tooltip.svelte'
  import { resolveSourceLabel, type SourceLabels } from '../../../composables/sourceNames'

  let {
    topSources,
    sourceLabels,
    listMaxHeight,
    selectedSourceId,
    onSelect,
    onDeselect,
  }: {
    topSources: { sourceName: string; found: number; sourceId?: number }[]
    sourceLabels: SourceLabels
    listMaxHeight?: number
    selectedSourceId?: number
    onSelect: (sourceId: number) => void
    onDeselect: () => void
  } = $props()

  // Re-clicking the already-selected source toggles it off, mirroring the
  // Deselect button rather than being a no-op.
  function handleClick(sourceId: number) {
    if (selectedSourceId === sourceId) {
      onDeselect()
    } else {
      onSelect(sourceId)
    }
  }
</script>

<article class="card">
  <div class="head">
    <h3>{$tStore('views.dashboard.topSources')}</h3>
    {#if selectedSourceId !== undefined}
      <button type="button" class="btn btn--secondary btn--sm" onclick={onDeselect}>
        {$tStore('views.dashboard.topSourcesDeselect')}
      </button>
    {/if}
  </div>
  {#if topSources.length === 0}
    <p class="muted">—</p>
  {:else}
    <ul
      class="sources"
      style={listMaxHeight !== undefined ? `max-height: ${listMaxHeight}px` : undefined}
    >
      {#each topSources as src (src.sourceName)}
        {@const active = src.sourceId !== undefined && src.sourceId === selectedSourceId}
        <li class:active>
          {#if src.sourceId !== undefined}
            {@const sourceId = src.sourceId}
            <Tooltip
              text={$tStore(
                active
                  ? 'views.dashboard.topSourcesDeselectTooltip'
                  : 'views.dashboard.topSourcesSelectTooltip',
              )}
            >
              <button
                type="button"
                class="source-row"
                aria-pressed={active}
                onclick={() => handleClick(sourceId)}
              >
                <span>{resolveSourceLabel(sourceLabels, src.sourceName)}</span>
                <span>{src.found}</span>
              </button>
            </Tooltip>
          {:else}
            <span class="source-row source-row--static">
              <span>{resolveSourceLabel(sourceLabels, src.sourceName)}</span>
              <span>{src.found}</span>
            </span>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</article>

<style lang="scss">
  @use '../../../assets/styles/variables' as v;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-sm;

    h3 {
      margin: 0;
    }
  }

  .sources {
    list-style: none;
    padding: 0;
    margin: v.$space-sm 0 0;

    // Falls back to a 6-row cap until RecentRunsCard's rendered height has
    // been measured (see DashboardView.svelte) and passed in as
    // `listMaxHeight` — matching that height (rather than a fixed row
    // count) is what keeps this list filling the card without leaving
    // blank space or inflating RecentRunsCard's own height in turn.
    max-height: calc(6 * (0.875rem * 1.5 + 2 * v.$space-sm));
    // Always scrollable by default — touch/no-hover input (see the media
    // query below) has no way to trigger a CSS :hover, so hiding overflow
    // until hover would make every source past the first 6 permanently
    // unreachable there, not just visually hidden.
    overflow-y: auto;

    // Only on inputs that can genuinely hover (mouse/trackpad) do we hide the
    // scrollbar until hover, for the compact "looks like a static list until
    // you reach for it" look the panel wants on desktop.
    @media (hover: hover) {
      overflow-y: hidden;

      &:hover {
        overflow-y: auto;
      }
    }

    li {
      &:nth-child(even) {
        background: v.$color-surface;
      }

      // Selected source — "Success" green (see .source-row) plus a left
      // accent bar so selection isn't signalled by colour alone.
      &.active {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: v.$color-success-fg;
        }
      }
    }
  }

  .source-row {
    display: flex;
    width: 100%;
    justify-content: space-between;
    font-size: 0.875rem;
    padding: v.$space-sm;
    font-family: inherit;
    color: inherit;
    background: none;
    border: none;
    cursor: pointer;
    text-align: inherit;

    // Hover only applies to the clickable button, so the static (no
    // sourceId) rows stay inert.
    &:hover:not(.source-row--static) {
      @include v.interactive-hover;
    }

    // Selected wins over hover (higher specificity), so hovering the
    // selected row keeps the stronger green.
    li.active &,
    li.active &:hover {
      @include v.interactive-selected;
    }
  }

  .source-row--static {
    cursor: default;
  }
</style>
